import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import React, { useState } from "react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
} from "recharts";
import { cn } from "@repo/core";
import { ChartExportFrame } from "../components/ChartExportFrame";
import { AppCard, KpiCard, KpiIcons, Money, ProgressBar, SectionTitle } from "../components/ui/AppCard";
import { useCurrency } from "../hooks/useCurrency";
import { useMonthCategorySummary } from "../hooks/useBudgetData";
import { monthShortLabel } from "../i18n/dates";
import { categoryLabel, groupLabel } from "../i18n/labels";
import { useT } from "../i18n/useT";
import { exportDonutChartPng, resolveThemeChartColors } from "../lib/exportUtils";
import { exportMonthlyBudgetFromDb } from "../lib/pdf/exportFromDb";
import { budgetLineTone, SEMANTIC } from "../lib/semanticColors";
import type { CategorySummary } from "../lib/schema";

interface Props { year: number; person: string }

const GROUP_ACCENT: Record<string, string> = {
  income:  "hsl(142 60% 40%)",
  needs:   "hsl(var(--primary))",
  wants:   "hsl(32 80% 55%)",
  savings: "hsl(200 80% 50%)",
};

const DONUT_COLORS = [
  "hsl(176 45% 34%)",
  "hsl(32 80% 55%)",
  "hsl(200 80% 50%)",
  "hsl(10 55% 51%)",
  "hsl(280 50% 60%)",
  "hsl(142 60% 45%)",
  "hsl(45 90% 50%)",
  "hsl(190 60% 45%)",
];

function CategoryRow({ cat }: { cat: CategorySummary }) {
  const { format } = useCurrency();
  const { t } = useT();
  const tone = budgetLineTone(cat.group, cat.actual, cat.monthlyTarget);
  const overSpend = cat.group !== "savings" && cat.actual > cat.monthlyTarget && cat.monthlyTarget > 0;
  const overSave = cat.group === "savings" && cat.actual > cat.monthlyTarget && cat.monthlyTarget > 0;
  const pct = Math.min(150, cat.pct);
  const barColor =
    tone === "negative" ? SEMANTIC.negative.bar
    : tone === "positive" && (overSave || cat.group === "savings") ? SEMANTIC.positive.bar
    : tone === "caution" ? SEMANTIC.caution.bar
    : undefined;

  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
      <span className="text-base leading-none w-6 text-center shrink-0">{cat.icon ?? "•"}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-xs font-medium text-foreground truncate">{categoryLabel(cat, t)}</span>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] text-muted-foreground">{format(cat.monthlyTarget)}</span>
            <Money amount={cat.actual} className="text-xs" colored={overSpend} />
          </div>
        </div>
        <ProgressBar value={pct} showPct={false} height={5} color={barColor} />
      </div>
      <span className={cn("text-[10px] font-semibold w-20 text-right shrink-0", SEMANTIC[tone].text)}>
        {overSpend || overSave ? "▲" : "▼"} {format(Math.abs(cat.variance))}
      </span>
    </div>
  );
}

function DonutTooltip({ active, payload }: TooltipProps<number, string>) {
  const { format } = useCurrency();
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <div
      className="rounded-xl border border-border bg-card shadow-lg px-3 py-2 text-xs"
      style={{ boxShadow: "var(--card-shadow)" }}
    >
      <p className="font-semibold text-foreground">{item.name}</p>
      <p className="text-primary font-bold mt-0.5">{format(Number(item.value))}</p>
    </div>
  );
}

export function MonthlyBudgetPage({ year, person }: Props) {
  const { format, currency, locale } = useCurrency();
  const { t, locale: uiLocale } = useT();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [exporting, setExporting] = useState(false);
  const { income, expense } = useMonthCategorySummary(year, month, person);

  const totalIncomeTarget  = income.reduce((s, c) => s + c.monthlyTarget, 0);
  const totalIncomeActual  = income.reduce((s, c) => s + c.actual, 0);
  const totalExpenseTarget = expense.reduce((s, c) => s + c.monthlyTarget, 0);
  const totalExpenseActual = expense.reduce((s, c) => s + c.actual, 0);

  const groups: Record<string, CategorySummary[]> = {};
  expense.forEach((c) => {
    if (!groups[c.group]) groups[c.group] = [];
    groups[c.group].push(c);
  });

  const monthLabel = monthShortLabel(month, uiLocale, year);

  // Group-level breakdown for donut chart
  const groupPieData = (["needs", "wants", "savings"] as const)
    .map((g) => {
      const gCats = groups[g] ?? [];
      const val = gCats.reduce((s, c) => s + c.actual, 0);
      return {
        name: groupLabel(g, t),
        value: val,
        color: GROUP_ACCENT[g],
      };
    })
    .filter((d) => d.value > 0);

  // Top spending categories for donut
  const topExpensePieData = [...expense]
    .filter((c) => c.actual > 0)
    .sort((a, b) => b.actual - a.actual)
    .slice(0, 6)
    .map((c, i) => ({
      name: categoryLabel(c, t),
      value: c.actual,
      color: DONUT_COLORS[i % DONUT_COLORS.length],
    }));

  return (
    <div className="p-4 lg:p-6 space-y-5 w-full">

      {/* Month selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setMonth((m) => Math.max(1, m - 1))} className="app-btn-outline p-2">
          <ChevronLeft size={15} />
        </button>
        <div className="flex gap-1 flex-wrap">
          {Array.from({ length: 12 }, (_, i) => (
            <button
              key={i}
              onClick={() => setMonth(i + 1)}
              className={cn(
                "px-2.5 py-1 text-xs font-medium rounded-md border transition-all",
                month === i + 1
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground bg-background"
              )}
            >
              {monthShortLabel(i + 1, uiLocale, year)}
            </button>
          ))}
        </div>
        <button onClick={() => setMonth((m) => Math.min(12, m + 1))} className="app-btn-outline p-2">
          <ChevronRight size={15} />
        </button>
        <button
          type="button"
          className="app-btn-outline text-xs ml-auto no-print min-h-[40px]"
          disabled={exporting}
          onClick={() => {
            setExporting(true);
            void exportMonthlyBudgetFromDb({
              year,
              month,
              person,
              currency,
              locale,
              uiLocale,
            }).finally(() =>
              setExporting(false)
            );
          }}
        >
          <Download size={13} /> {exporting ? "…" : t("mb.exportMonth")}
        </button>
      </div>

      {/* Summary totals */}
      <div className="grid grid-cols-2 gap-3">
        <KpiCard
          label={t("mb.income")}
          value={format(totalIncomeActual, true)}
          sub={t("mb.target", { amount: format(totalIncomeTarget) })}
          color="mint"
          icon={<KpiIcons.moneyIn size={16} />}
          index={0}
        >
          <ProgressBar
            value={(totalIncomeActual / Math.max(totalIncomeTarget, 1)) * 100}
            showPct
            height={5}
            color={SEMANTIC.positive.bar}
          />
        </KpiCard>
        <KpiCard
          label={t("mb.expenses")}
          value={format(totalExpenseActual, true)}
          sub={t("mb.budget", { amount: format(totalExpenseTarget) })}
          color="coral"
          icon={<KpiIcons.moneyOut size={16} />}
          index={1}
        >
          <ProgressBar
            value={(totalExpenseActual / Math.max(totalExpenseTarget, 1)) * 100}
            showPct
            height={5}
            color={SEMANTIC.negative.bar}
          />
        </KpiCard>
      </div>

      {/* Donut Chart visual breakdown */}
      {totalExpenseActual > 0 && (
        <AppCard padding="md">
          <ChartExportFrame
            title={t("mb.allocation")}
            onExport={() => {
              const theme = resolveThemeChartColors();
              const colorFor = (name: string) => {
                if (name === groupLabel("needs", t) || name === "Needs") return theme.primary;
                if (name === groupLabel("wants", t) || name === "Wants") return "hsl(32 80% 55%)";
                return "hsl(200 80% 50%)";
              };
              exportDonutChartPng({
                filename: `spending-allocation-${year}-${month}`,
                title: `${t("mb.allocation")} · ${monthLabel} ${year}`,
                slices: groupPieData.map((g) => ({
                  label: g.name,
                  value: g.value,
                  color: colorFor(g.name),
                })),
                watermark: `BudgetOS · ${monthLabel} ${year}`,
              });
            }}
          >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            {/* Group donut */}
            <div className="flex flex-col items-center">
              <p className="text-xs font-medium text-muted-foreground mb-1">{t("mb.byGroup")}</p>
              <div className="w-full h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={groupPieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={45}
                      outerRadius={65}
                      paddingAngle={3}
                    >
                      {groupPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} stroke="transparent" />
                      ))}
                    </Pie>
                    <Tooltip content={<DonutTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex gap-4 justify-center flex-wrap mt-1">
                {groupPieData.map((g) => (
                  <div key={g.name} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: g.color }} />
                    <span>{g.name}: {format(g.value, true)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Categories Donut */}
            <div className="flex flex-col items-center">
              <p className="text-xs font-medium text-muted-foreground mb-1">{t("mb.topExpense")}</p>
              <div className="w-full h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={topExpensePieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={45}
                      outerRadius={65}
                      paddingAngle={3}
                    >
                      {topExpensePieData.map((entry, index) => (
                        <Cell key={`cat-cell-${index}`} fill={entry.color} stroke="transparent" />
                      ))}
                    </Pie>
                    <Tooltip content={<DonutTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex gap-3 justify-center flex-wrap mt-1">
                {topExpensePieData.map((c) => (
                  <div key={c.name} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: c.color }} />
                    <span className="truncate max-w-[100px]">{c.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </ChartExportFrame>
        </AppCard>
      )}

      {/* Income categories */}
      <div>
        <SectionTitle>{t("mb.incomeSources")}</SectionTitle>
        <AppCard padding="md">
          {income.map((c) => <CategoryRow key={c.id} cat={c} />)}
        </AppCard>
      </div>

      {/* Expense groups */}
      {(["needs","wants","savings"] as const).map((group) => {
        const cats = groups[group] ?? [];
        if (!cats.length) return null;
        const title = groupLabel(group, t);
        const groupTarget = cats.reduce((s, c) => s + c.monthlyTarget, 0);
        const groupActual = cats.reduce((s, c) => s + c.actual, 0);
        const accent      = GROUP_ACCENT[group];
        return (
          <div key={group}>
            <div className="flex items-center justify-between mb-2">
              <SectionTitle className="mb-0">{title}</SectionTitle>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Money amount={groupActual} className="text-xs" />
                <span>/</span>
                <span>{format(groupTarget)}</span>
              </div>
            </div>
            <AppCard padding="none" className="overflow-hidden">
              <div className="h-1 w-full" style={{ backgroundColor: accent }} />
              <div className="p-4">
                {cats.map((c) => <CategoryRow key={c.id} cat={c} />)}
              </div>
            </AppCard>
          </div>
        );
      })}
    </div>
  );
}
